import React, { useEffect, useState, useRef, useImperativeHandle } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Table as ResTable, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import { exportType, fetchExportData } from './utils';
import RowFieldDisplay from './RowFieldDisplay/index.js';
import SubTable from '../SubTable/index.js';
import {
    CircularLoading,
    PrimaryButton,
    PageTitleWithToolTip,
    CommonTableFilterList,
    AddButton,
    BulkModal,
} from '../../Common';
import {
    Grid,
    Typography,
    Card,
    CardHeader,
    TablePagination,
    tablePaginationClasses,
    Box,
    IconButton,
    Tooltip,
    Stack,
} from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UserInfo from '../../../UserInfo';
import { trakrefFontFamily, trakrefFontFamilySemiBold } from '../../../utils';
import { getDefaultPage, getDefaultSortBy, getDefaultFilters } from './utils';
import { Grey2 } from '../../../color_utils.js';
import { TranslateW } from '../../../Utils/Languages/Translate.js';
import Text from "../../../Utils/Util_Components/Translator";
import NoTableDataIcon from '../../../Icons/empty_table_placeholder.svg';
import RowHeaderFilter from './RowHeaderFilter.js';
import useBulkUpdates from '../../../hooks/useBulkUpdates.js';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import './Table.css';

const Table = ({
    tableId,
    url,
    defaultSort,
    rowCount,
    page,
    onRowsUpdated,
    children,
    refresh,
    onFetchSuccess,
    isAllSelected,
    DataCallback,
    exportButtonURL,
    exportButtonAlt,
    fileName,
    printButtonURL,
    printButtonAlt,
    customizeColumnVisible,
    noResultText,
    noResultText2,
    noResultText3,
    MUIButtonText,
    MUIButtonOnClick,
    disableMUIButton,
    showColumnHeaderFilter,
    tableHeader,
    tableHeaderTooltipText,
    renderDefaultFilter,
    renderDefaultFilterInTable,
    tableWithoutTitle,
    filterOptions,
    filterBy,
    setFilterBy,
    rowActions,
    subTableColumns,
    subtableOpen,
    subtableParentId,
    renderBatchUpdate,
    batchUpdateType,
    batchUpdateExportText,
    batchUpdateImportText,
    columnHeaderFilter,
    exportFileRef,
    searchKeyword,
    searchById,
    isOrCondition,
    showFilters,
    hideExportButton,
    tableContainerSx,
    updatedRow
}) => {
    const wrapperRef = useRef();
    const location = useLocation();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [smallLoading, setSmallLoading] = useState(true);
    const [checkedRows, setCheckedRows] = useState([]);
    const [rows, setRows] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(rowCount);
    const [noData, setNoData] = useState(false);
    const [clearFilters, setClearFilters] = useState(sessionStorage.getItem(url + "filters" + UserInfo.instanceId) == null ? false : true);
    const [filterListOptions, setFilterListOptions] = useState([]);
    const [resultCount, setResultCount] = useState(0);
    const [showSelectColumns, setShowSelectColumns] = useState(false);
    const [showSelectColumnsList, setShowSelectColumnsList] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [hasInitialData, setHasInitialData] = useState({
        initialize: false,
        value: false
    });

    const LOCAL_STORAGE_COLUMNS = tableId ? `${tableId}columns${UserInfo.instanceId}` : `${url}columns${UserInfo.instanceId}`;

    const [tableSearchKeyword, setTableSearchKeyword] = useState(searchKeyword);
    useEffect(() => {
        setTableSearchKeyword(searchKeyword);
    }, [searchKeyword]);

    const [tableRequest, setTableRequest] = useState({
        page: getDefaultPage(url, page),
        sortBy: getDefaultSortBy(url, defaultSort),
        filters: getDefaultFilters(url, tableSearchKeyword, searchById, isOrCondition),
    });

    const [exportLoading, setExportLoading] = useState({
        exportPdf: false,
        exportCsv: false
    });

    const [batchUpdateModelOpen, setBatchUpdateModelOpen] = useState(false);

    const { isLoading, BatchUpdateExport, BatchUpdateImport } = useBulkUpdates();

    useEffect(() => {
        if (onRowsUpdated) {
            onRowsUpdated(checkedRows);
        }
    }, [checkedRows]);

    useEffect(() => {
        const value = isAllSelected ? rows : [];
        setCheckedRows(value);
    }, [isAllSelected])

    // Determine filter options
    useEffect(() => {
        const filterListOptions = [];
        children.map(column => {
            if (column.props.filter) {
                filterListOptions.push({
                    translateId: column.props.id,
                    type: column.props.filter,
                    property: column.props.property,
                    name: column.props.title,
                    initialValue: column.props.initialValue
                });
            }
        });
        setFilterListOptions(filterListOptions);
    }, []);

    useEffect(() => {
        const appliedColumns = JSON.parse(localStorage.getItem(LOCAL_STORAGE_COLUMNS));

        if (appliedColumns) {
            setShowSelectColumnsList(changeDisplayWeight(appliedColumns));
        } else {
            // Find any columns
            children.map(column => {
                if (column.props) {
                    showSelectColumnsList.push({
                        translateId: column.props.id,
                        property: column.props.property,
                        title: column.props.title,
                        visible: column.props.visibleForFilter || column.props.visible === false ? false : true,
                        visibleForFilter: column.props.visibleForFilter
                    });
                }
            });
            setShowSelectColumnsList(showSelectColumnsList);
        }
        // Bind event for clicks outside element
        document.addEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        handleSearchKeyword();
    }, [tableSearchKeyword]);

    useEffect(() => {
        setSmallLoading(true);
        fetchData();
    }, [tableRequest, refresh, url]);
    useEffect(() => {
        if (updatedRow) {
            setRows(updatedRow);
        }
    }, [updatedRow]);

    const handleSearchKeyword = () => {
        if (tableSearchKeyword) {
            setTableRequest(prev => ({
                ...prev,
                filters: getDefaultFilters(url, tableSearchKeyword, searchById, isOrCondition)
            }));
        }
        // clear the search keyword after it save to sessionStorage
        setTableSearchKeyword('');
    };

    const changeDisplayWeight = (newColumn) => {
        const columns = [...newColumn];
        columns.forEach(item => {
            if (item.title) {
                item.title = item.title.replace(/\((Kgs|Lbs)\)/gi, `(${UserInfo.getWeightUnits()})`);
            }
        });
        return columns;
    };

    const fetchData = async () => {
        try {
            if (noData === true) {
                setLoading(true);
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    rowCount: rowsPerPage,
                    page: tableRequest.page,
                    filters: tableRequest.filters,
                    sortBy: tableRequest.sortBy
                })
            });

            const data = await response.json();

            if (onFetchSuccess) {
                onFetchSuccess(data);
            }

            setCheckedRows(isAllSelected ? data.rows : []);

            setRows(data.rows);
            setNoData(data.rows <= 0);

            setResultCount(data.resultCount);
            setSmallLoading(false);

            if (!hasInitialData.initialize || !hasInitialData.value || data.rows <= 0) {
                setHasInitialData({
                    initialize: true,
                    value: !(data.rows <= 0)
                });
            }

            if (DataCallback) {
                DataCallback(data);
            }
        }
        catch {
            setLoading(false);
        }
        finally {
            setLoading(false);
        }
    };

    const headerClicked = (property) => {
        if (property) {
            if (tableRequest.sortBy === property) {
                sessionStorage.setItem(url + "sort" + UserInfo.instanceId, JSON.stringify(property + '_desc'));
                setTableRequest(prev => ({
                    ...prev,
                    sortBy: property + '_desc'
                }));
            } else {
                sessionStorage.setItem(url + "sort" + UserInfo.instanceId, JSON.stringify(property));
                setTableRequest(prev => ({
                    ...prev,
                    sortBy: property
                }));
            }

            sessionStorage.setItem(url + "page" + UserInfo.instanceId, JSON.stringify(1));
            setTableRequest(prev => ({
                ...prev,
                page: 1
            }));
        }
    };

    useEffect(() => {
        var locationState = location.state;
        if (filterListOptions.length &&
            locationState &&
            locationState.titleToFilter &&
            locationState.propertyToFilter &&
            locationState.typeToFilter &&
            locationState.valueToFilter) {
            if (tableRequest.filters.length > 0) {
                setTableRequest({
                    sortBy: "",
                    page: 1,
                    filters: []
                });
            }
            else if (tableRequest.filters.length <= 0) {
                sessionStorage.removeItem(url + "filters" + UserInfo.instanceId);
                history.replace({ ...location, state: { from: location.state.from } });
                handleFilterKeyUp({
                    keyCode: 13,
                    target: {
                        id: locationState.propertyToFilter,
                        value: locationState.valueToFilter
                    }
                });
            }
        }
    }, [location.state, filterListOptions, tableRequest.filters])

    useEffect(() => {
        if (!filterBy || Object.keys(filterBy).length === 0)
            return;

        if (tableRequest.filters.length > 0) {
            setTableRequest({
                sortBy: "",
                page: 1,
                filters: []
            });
            sessionStorage.removeItem(url + "filters" + UserInfo.instanceId)
        }
        else if (tableRequest.filters.length <= 0) {
            handleFilterKeyUp({
                keyCode: 13,
                target: filterBy
            })
            setFilterBy({})
        }
    }, [filterBy, filterOptions, tableRequest.filters])


    const handleFilterKeyUp = (evt) => {
        if (evt.keyCode == 13) {
            let tempArray = JSON.parse(sessionStorage.getItem(url + "filters" + UserInfo.instanceId));
            if (tempArray == null)
                tempArray = [...tableRequest.filters];

            let filter = {
                property: evt.target.id,
                name: filterListOptions.filter(f => f.property === evt.target.id)[0].name,
                type: "text",
                value: evt.target.value
            };

            evt.target.value = "";

            tempArray.push(filter);

            sessionStorage.setItem(url + "filters" + UserInfo.instanceId, JSON.stringify(tempArray));
            sessionStorage.setItem(url + "page" + UserInfo.instanceId, JSON.stringify(1));

            setTableRequest(prev => ({
                ...prev,
                page: 1,
                filters: tempArray
            }));
            setSelectedFilter(null);
            setClearFilters(tempArray.length > 0 ? true : false);
        }
    };

    const handleClearFilters = (e) => {
        e.stopPropagation();
        sessionStorage.removeItem(url + "filters" + UserInfo.instanceId);
        setTableRequest({
            sortBy: "",
            page: 1,
            filters: []
        });
        setClearFilters(false);
    };

    const changePage = (amount) => {
        sessionStorage.setItem(url + "page" + UserInfo.instanceId, JSON.stringify(tableRequest.page + amount));
        setTableRequest(prev => ({
            ...prev,
            page: prev.page + amount
        }));
    };

    const handleFirstPageButtonClick = () => changePage(-tableRequest.page + 1);

    const handleLastPageButtonClick = () => changePage(Math.max(0, Math.ceil(resultCount / rowsPerPage) - tableRequest.page));

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setTableRequest(prev => ({ ...prev, page: 1 }));
    };

    const changePageBack = () => changePage(-1);

    const changePageForward = () => changePage(1);

    const handleFilterClick = evt => evt.stopPropagation();

    const handleClickOutside = (event) => {
        if (wrapperRef && wrapperRef.current && !wrapperRef.current.contains(event.target)) {
            setShowSelectColumns(false);
        }
    };

    const addSelectedColumn = (_, newColumn) => {
        const columns = [...showSelectColumnsList];
        let col = columns.find(f => f.property == newColumn.property);
        col.visible = !col.visible;

        setShowSelectColumnsList(columns);

        localStorage.setItem(LOCAL_STORAGE_COLUMNS, JSON.stringify(showSelectColumnsList));
    };

    const getColumn = (property) => {
        const errorTypes = [undefined, null];
        const foundColumn = showSelectColumnsList.find(f => f.property === property);
        // if column cannot be found, do not show the column
        if (errorTypes.includes(foundColumn)) {
            return {};
        }
        return foundColumn;
    };

    const exportFile = async (e, type) => {
        setExportLoading({
            exportPdf: type == exportType.pdf,
            exportCsv: type == exportType.csv
        });
        await fetchExportData(
            e,
            {
                rowsPerPage,
                export_url: type == exportType.pdf ? printButtonURL : exportButtonURL,
                page: tableRequest.page,
                sort: tableRequest.sortBy,
                filters: tableRequest.filters,
                type: type
            },
            fileName
        );
        setExportLoading({
            exportPdf: false,
            exportCsv: false,
        });
    };

    useImperativeHandle(exportFileRef, () => ({
        triggerExport(e, type) {
            exportFile(e, type);
        }
    }));

    /* const TableHeaderAction = () => {
        if (MUIButtonText) {
            return (
                <div className="table-header-buttons flex f-je">
                    <AddButton
                        text={MUIButtonText}
                        onClick={() => MUIButtonOnClick()}
                        sx={{
                            height: "33px !important"
                        }}
                        disabled={disableMUIButton}
                    />
                </div>
            );
        }

        if (renderDefaultFilter && renderDefaultFilterInTable) {
            return (
                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ marginLeft: '2%' }}
                >
                    <CommonTableFilterList
                        tableURL={url}
                        filters={filterListOptions}
                        filterValues={filterOptions}
                        tableRequest={tableRequest}
                        setTableRequest={setTableRequest}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        clearFilters={clearFilters}
                        handleClearFilters={handleClearFilters}
                        setClearFilters={setClearFilters}
                    />

                    <TableActions />
                </Stack>
            );
        }

        return null;
    }; */

    const TableActions = () => (
        <>
            <div className="action-buttons">
                {
                    UserInfo.isSystemAdministrator() && renderBatchUpdate && (
                        <>
                            <Tooltip title={batchUpdateExportText}>
                                <IconButton
                                    className='IconButton'
                                    onClick={(_) => {
                                        BatchUpdateExport(
                                            batchUpdateType,
                                            { sort: tableRequest.sortBy, filters: tableRequest.filters, },
                                        );
                                    }}
                                >
                                    <img
                                        src="/images/batchUpdateExport.svg"
                                        alt={TranslateW("BatchUpdateExport")}
                                    />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={batchUpdateImportText}>
                                <IconButton
                                    className='IconButton'
                                    onClick={_ => setBatchUpdateModelOpen(true)}
                                >
                                    <img
                                        src="/images/batchUpdateImport.svg"
                                        alt={TranslateW("BatchUpdateExport")}
                                    />
                                </IconButton>
                            </Tooltip>
                        </>
                    )
                }
                {(printButtonURL && !hideExportButton) && (
                    <Tooltip title={printButtonAlt}>
                        {exportLoading.exportPdf ? (
                            <CircularLoading size={24} marginLeft={0} />
                        ) : (
                            <IconButton
                                className="IconButton"
                                onClick={e => exportFile(e, exportType.pdf)}
                            >
                                <img
                                    src="/images/ic_print_outline.svg"
                                    alt="Print"
                                />
                            </IconButton>
                        )}
                    </Tooltip>
                )}
                {(exportButtonURL && !hideExportButton) && (
                    <Tooltip title={exportButtonAlt}>
                        {exportLoading.exportCsv ? (
                            <CircularLoading size={24} marginLeft={0} />
                        ) : (
                            <IconButton className='IconButton' onClick={e => exportFile(e, exportType.csv)}>
                                <img
                                    src="/images/ic_export1.svg"
                                    alt="Print"
                                />
                            </IconButton>
                        )}
                    </Tooltip>
                )}
                {customizeColumnVisible && (
                    <Tooltip title={TranslateW("customizeColumns")}>
                        <IconButton className='IconButton' onClick={() => setShowSelectColumns(!showSelectColumns)}>
                            <img
                                src="/images/ic_customize_outline.svg"
                                alt={TranslateW("customizeColumns")}
                            />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
            {showSelectColumns && (
                /* className="columns-choose cols-xs-12 col-md-3" */
                <div className={`columns-choose${renderDefaultFilterInTable ? '-filtersInTable' : ''} cols-xs-12 col-md-3`} ref={wrapperRef}>
                    <div className="createColumns-toptext">
                        <div className="createColumns-apply-text">
                            {TranslateW("columnCustomizationHelperText")}
                        </div>
                    </div>
                    <div className="columns-choose-choices">
                        {showSelectColumnsList.filter(x => !x.visibleForFilter).map((column) => (
                            column.property && (
                                <div className="filter-create-box col-xs-12 col-md-6" onClick={e => addSelectedColumn(e, column)}>
                                    <input type="checkbox" checked={column.visible} className='columnCheckbox' />
                                    <Text id={column.translateId} text={column.title} />
                                </div>
                            )
                        ))}
                    </div>
                </div>
            )}
        </>
    );

    const NoData = () => (
        <Card variant='outlined'>
            <Grid container>
                <Grid item xs={12} align='center'>
                    <img
                        style={{ paddingTop: '4%' }}
                        src={NoTableDataIcon}
                        alt={noResultText || "No results to display. Reset filters to see more results"}
                    />
                </Grid>
                <Grid item xs={12}>
                    {
                        noResultText ? (
                            <Typography
                                variant='h5'
                                align='center'
                                style={{
                                    marginTop: '2%',
                                    paddingBottom: noResultText2 ? '.5%' : '2%',
                                    color: '#1A173B',
                                    fontFamily: trakrefFontFamilySemiBold,
                                }}
                            >
                                {noResultText}
                            </Typography>
                        ) : (
                            <Typography
                                variant='h5'
                                align='center'
                                style={{
                                    marginTop: '2%',
                                    paddingBottom: '2%',
                                    color: '#1A173B',
                                    fontFamily: trakrefFontFamilySemiBold
                                }}
                            >
                                No results to display. Reset filters to see more results
                            </Typography>
                        )
                    }
                </Grid>
                {
                    (noResultText2 && !noResultText3) ? (
                        <Grid item xs={12}>
                            <Typography
                                variant='h5'
                                align='center'
                                style={{
                                    paddingBottom: MUIButtonText ? '1.5%' : '2%',
                                    color: '#1A173B',
                                    fontSize: '1.25rem',
                                    fontFamily: trakrefFontFamily
                                }}
                            >
                                {noResultText2}
                            </Typography>
                        </Grid>
                    ) :

                        (noResultText2 && !tableRequest.filters.length && hasInitialData.initialize && !hasInitialData.value) ? (
                            <Grid item xs={12}>
                                <Typography
                                    variant='h5'
                                    align='center'
                                    style={{
                                        paddingBottom: MUIButtonText ? '1.5%' : '2%',
                                        color: '#1A173B',
                                        fontSize: '1.25rem',
                                        fontFamily: trakrefFontFamily
                                    }}
                                >
                                    {noResultText2}
                                </Typography>
                            </Grid>
                        ) : (
                            (noResultText3 && (!!tableRequest.filters.length)) && (
                                <Grid item xs={12}>
                                    <Typography
                                        variant='h5'
                                        align='center'
                                        style={{
                                            paddingBottom: MUIButtonText ? '1.5%' : '2%',
                                            color: '#1A173B',
                                            fontSize: '1.25rem',
                                            fontFamily: trakrefFontFamily
                                        }}
                                    >
                                        {noResultText3}
                                    </Typography>
                                </Grid>
                            )
                        )
                }
                {(MUIButtonText && !renderDefaultFilterInTable) && (
                    <Grid item xs={12} align='center' sx={{ marginBottom: '3%' }}>
                        <PrimaryButton
                            buttonText={MUIButtonText}
                            onClick={() => MUIButtonOnClick()}
                            disabled={UserInfo.isReadOnly()}
                            width={20}
                        />
                    </Grid>
                )}
                {(renderDefaultFilterInTable && tableRequest.filters.length > 0) && (
                    <Grid item xs={12} align='center' sx={{ marginBottom: '3%' }}>
                        <PrimaryButton
                            buttonText="Clear Filters"
                            onClick={e => handleClearFilters(e)}
                            width={20}
                        />
                    </Grid>
                )}
            </Grid>
        </Card>
    );

    const PaginationActions = () => (
        <Box sx={{ flexShrink: 0 }}>
            <Tooltip title='First Page'>
                <IconButton
                    onClick={handleFirstPageButtonClick}
                    disabled={tableRequest.page === 1}
                    aria-label="first page"
                >
                    <FirstPageIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title='Previous Page'>
                <IconButton
                    onClick={changePageBack}
                    disabled={tableRequest.page === 1}
                    aria-label="previous page"
                >
                    <KeyboardArrowLeft />
                </IconButton>
            </Tooltip>
            <Tooltip title='Next Page'>
                <IconButton
                    onClick={changePageForward}
                    disabled={tableRequest.page > Math.ceil(resultCount / rowsPerPage) - 1}
                    aria-label="next page"
                >
                    <KeyboardArrowRight />
                </IconButton>
            </Tooltip>
            <Tooltip title='Last Page'>
                <IconButton
                    onClick={handleLastPageButtonClick}
                    disabled={tableRequest.page > Math.ceil(resultCount / rowsPerPage) - 1}
                    aria-label="last page"
                >
                    <LastPageIcon />
                </IconButton>
            </Tooltip>
            {/* Input field for page number - might need this in the future */}
            {/* <Input
                label='Page'
                variant='standard'
                value={tableRequest.page}
                onChange={e => {
                    // changePage(-tableRequest.page + 1 + Number(e.target.value))
                    console.log('value', e.target.value)
                }}
            /> */}
        </Box>
    );

    const Pagination = () => (
        <TablePagination
            sx={{
                [`& .${tablePaginationClasses.selectLabel}`]: { fontFamily: trakrefFontFamilySemiBold },
                [`& .${tablePaginationClasses.displayedRows}`]: { fontFamily: trakrefFontFamilySemiBold },
                [`& .${tablePaginationClasses.spacer}`]: { display: "none" },
                [`& .${tablePaginationClasses.toolbar}`]: { justifyContent: "center", paddingLeft: 0 }
            }}
            rowsPerPageOptions={[5, 10, 50, 100]}
            count={resultCount}
            rowsPerPage={rowsPerPage}
            // MUI TablePagination is built using a starting index of 0..
            // ..but we start at 1, so must subtract 1 in order to see proper results 
            page={tableRequest.page - 1}
            slotProps={{
                select: {
                    inputProps: {
                        'aria-label': 'rows per page',
                    },
                },
            }}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={PaginationActions}
        />
    );

    const Table = () => (
        <Card variant='outlined' className="table" sx={{...tableContainerSx}}>
            {tableHeader && (
                <CardHeader
                    title={
                        <Stack direction='row' justifyContent='space-between' alignItems='center'>
                            <PageTitleWithToolTip
                                pageTitle={tableHeader}
                                toolTipText={tableHeaderTooltipText}
                            />
                            {
                                MUIButtonText && (
                                    <div className="table-header-buttons flex f-je">
                                        <AddButton
                                            text={MUIButtonText}
                                            onClick={() => MUIButtonOnClick()}
                                            sx={{
                                                height: "33px !important"
                                            }}
                                            disabled={disableMUIButton}
                                        />
                                    </div>
                                )
                            }
                        </Stack>
                    }
                    sx={{
                        "& .MuiCardHeader-action": { display: renderDefaultFilterInTable ? "contents" : "flex" },
                        "& .MuiCardHeader-content": { maxWidth: renderDefaultFilterInTable ? "fit-content" : "100%", textWrap: "nowrap" }
                    }}
                />
            )}
            <ResTable>
                <Thead>
                    <Tr className="table-columnheader">
                        {children.map(column => (
                            <Th
                                onClick={() => {
                                    if (column.props.noSort != 'true') {
                                        headerClicked(column.props.property)
                                    }
                                }}
                                width={column.props.width}
                                className={(column.props.noSort ? 'noSort' : '') + (column.props.icon ? ' hasIcon' : '') + ((getColumn(column.props.property)).visible === false ? ' hideColumn' : '')}
                            >
                                {
                                    !column.props.hideColumnName && (
                                        <>
                                            {
                                                column.props.id ? (
                                                    <Text
                                                        id={column.props.id}
                                                        text={column.props.title}
                                                    />
                                                ) : (
                                                    column.props.title
                                                )
                                            }
                                        </>
                                    )
                                }
                                {column.props.property === tableRequest.sortBy && (
                                    <ExpandMoreIcon className='sortIcon' />
                                )}
                                {(column.props.property + '_desc') === tableRequest.sortBy && (
                                    <ExpandLessIcon className='sortIcon' />
                                )}
                            </Th>
                        ))}
                    </Tr>
                </Thead>

                <Tbody>
                    {showColumnHeaderFilter && (
                        <RowHeaderFilter
                            children={children}
                            getColumn={getColumn}
                            handleFilterKeyUp={handleFilterKeyUp}
                            handleFilterClick={handleFilterClick}
                            filterProps={columnHeaderFilter}
                        />
                    )}

                    {smallLoading ? (
                        <tr>
                            <td colSpan={children.length} style={{ height: rowsPerPage * 2.8 + 'rem' }}>
                                <CircularLoading />
                            </td>
                        </tr>
                    ) : (
                        rows.map((row, index) => (
                            <>
                                <Tr
                                    key={row['key']}
                                    className={((index % 2) === 0) ? 'even-row' : ''}
                                >
                                    {children.map(column => (
                                        <RowFieldDisplay
                                            column={column}
                                            row={row}
                                            rows={rows}
                                            checkedRows={checkedRows}
                                            addCheckedRows={(asset) => setCheckedRows([
                                                ...checkedRows,
                                                asset
                                            ])}
                                            removeCheckedRows={(asset) => setCheckedRows(
                                                checkedRows.filter(r => r.id !== asset.id)
                                            )}
                                            index={index}
                                            showSelectColumnsList={showSelectColumnsList}
                                        />
                                    ))}
                                    {!!rowActions.length && (
                                        <Td className="row-actions">
                                            {rowActions.map(({ isVisible, toolTip, icon, onClick, disabled = () => false }) => {
                                                const renderIcon = isVisible(row);
                                                const iconState = disabled(row);
                                                const Icon = icon(row);
                                                if (renderIcon) {
                                                    return (
                                                        toolTip ? (
                                                            <Tooltip
                                                                title={toolTip}
                                                            >
                                                                <IconButton
                                                                    disabled={iconState}
                                                                    onClick={() => onClick(row)}
                                                                >
                                                                    <Icon sx={{ color: iconState ? "disabled" : Grey2 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : (
                                                            <IconButton
                                                                onClick={() => onClick(row)}
                                                            >
                                                                <Icon sx={{ color: Grey2 }} />
                                                            </IconButton>
                                                        )
                                                    );
                                                }

                                                return <IconButton disabled sx={{ width: "40px", height: "40px", padding: "8px" }} />;
                                            })
                                            }
                                        </Td>
                                    )}
                                </Tr>

                                {(
                                    subTableColumns
                                    && subtableParentId === row["id"]
                                    && subtableOpen
                                ) && (
                                        <Tr>
                                            <Td colSpan={children.length} className="subtable">
                                                <SubTable
                                                    parentEntityId={row["id"]}
                                                    columns={subTableColumns}
                                                    open={subtableOpen}
                                                />
                                            </Td>
                                        </Tr>
                                    )}
                            </>
                        ))
                    )}
                </Tbody>

                <Pagination />
            </ResTable>
        </Card>
    );

    if (loading) {
        return (
            <div className="tablediv">
                <CircularLoading size={35} marginTop={1} />
            </div>
        );
    }

    // Table filters being inside of the table component..
    // ..were too deeply nested so I had to lift them up..
    // ..this is just a bandaid solution but since we are deprecating..
    // ..the old table component in the near future this will work for now
    if (renderDefaultFilter && renderDefaultFilterInTable) {
        if (noData === true) return <NoData />;

        return (
            <Card
                variant='outlined'
                className="table"
                sx={{
                    ...(tableWithoutTitle && ({
                        borderRadius: '0px', borderBottom: 'none', borderRight: 'none', borderLeft: 'none'
                    }))
                }}
            >
                <CardHeader
                    {
                    ...(tableHeader && {
                        title: <PageTitleWithToolTip
                            pageTitle={tableHeader}
                            toolTipText={tableHeaderTooltipText}
                        />
                    })
                    }
                    action={
                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            alignItems="center"
                            sx={{ ...(!tableWithoutTitle && ({ marginLeft: '2%' })) }}
                        >
                            <CommonTableFilterList
                                tableURL={url}
                                filters={filterListOptions}
                                filterValues={filterOptions}
                                tableRequest={tableRequest}
                                setTableRequest={setTableRequest}
                                selectedFilter={selectedFilter}
                                setSelectedFilter={setSelectedFilter}
                                clearFilters={clearFilters}
                                handleClearFilters={handleClearFilters}
                                setClearFilters={setClearFilters}
                            />

                            <TableActions />
                        </Stack>
                    }
                    sx={{
                        ...(tableWithoutTitle && ({
                            padding: '5px 16px 5px 10px',
                            borderBottom: '1px solid rgba(216, 216, 216, 1)'
                        })),
                        "& .MuiCardHeader-action": { display: renderDefaultFilterInTable ? "contents" : "flex" },
                        "& .MuiCardHeader-content": { maxWidth: renderDefaultFilterInTable ? "fit-content" : "100%", textWrap: "nowrap" }
                    }}
                />
                <ResTable>
                    <Thead>
                        <Tr className="table-columnheader">
                            {children.map(column => (
                                <Th
                                    onClick={() => {
                                        if (column.props.noSort != 'true') {
                                            headerClicked(column.props.property)
                                        }
                                    }}
                                    width={column.props.width}
                                    className={(column.props.noSort ? 'noSort' : '') + (column.props.icon ? ' hasIcon' : '') + ((getColumn(column.props.property)).visible === false ? ' hideColumn' : '')}
                                >
                                    {column.props.id ? (
                                        <Text
                                            id={column.props.id}
                                            text={column.props.title}
                                        />
                                    ) : (
                                        column.props.title
                                    )}

                                    {column.props.property === tableRequest.sortBy && (
                                        <ExpandMoreIcon className='sortIcon' />
                                    )}
                                    {(column.props.property + '_desc') === tableRequest.sortBy && (
                                        <ExpandLessIcon className='sortIcon' />
                                    )}
                                </Th>
                            ))}
                        </Tr>
                    </Thead>

                    <Tbody>
                        {showColumnHeaderFilter && (
                            <RowHeaderFilter
                                children={children}
                                getColumn={getColumn}
                                handleFilterKeyUp={handleFilterKeyUp}
                                handleFilterClick={handleFilterClick}
                                filterProps={columnHeaderFilter}
                            />
                        )}

                        {smallLoading ? (
                            <tr>
                                <td colSpan={children.length} style={{ height: rowsPerPage * 2.8 + 'rem' }}>
                                    <CircularLoading />
                                </td>
                            </tr>
                        ) : (
                            rows.map((row, index) => (
                                <>
                                    <Tr
                                        key={row['key']}
                                        className={((index % 2) === 0) ? 'even-row' : ''}
                                    >
                                        {children.map(column => (
                                            <RowFieldDisplay
                                                column={column}
                                                row={row}
                                                rows={rows}
                                                checkedRows={checkedRows}
                                                addCheckedRows={(asset) => setCheckedRows([
                                                    ...checkedRows,
                                                    asset
                                                ])}
                                                removeCheckedRows={(asset) => setCheckedRows(
                                                    checkedRows.filter(r => r.id !== asset.id)
                                                )}
                                                index={index}
                                                showSelectColumnsList={showSelectColumnsList}
                                            />
                                        ))}
                                        {!!rowActions.length && (
                                            <Td className="row-actions">
                                                {rowActions.map(({ isVisible, toolTip, icon, onClick }) => {
                                                    const renderIcon = isVisible(row);
                                                    const Icon = icon(row);
                                                    if (renderIcon) {
                                                        return (
                                                            toolTip ? (
                                                                <Tooltip
                                                                    title={toolTip}
                                                                >
                                                                    <IconButton
                                                                        onClick={() => onClick(row)}
                                                                    >
                                                                        <Icon sx={{ color: Grey2 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            ) : (
                                                                <IconButton
                                                                    onClick={() => onClick(row)}
                                                                >
                                                                    <Icon sx={{ color: Grey2 }} />
                                                                </IconButton>
                                                            )
                                                        );
                                                    }

                                                    return <IconButton disabled sx={{ width: "40px", height: "40px", padding: "8px" }} />;
                                                })
                                                }
                                            </Td>
                                        )}
                                    </Tr>
                                </>
                            ))
                        )}
                    </Tbody>

                    <Pagination />
                </ResTable>
            </Card>
        );
    }

    return (
        // Filter and custom column
        <>
            {
                renderDefaultFilter
                && !renderDefaultFilterInTable
                && filterListOptions.length > 0
                && (
                    <div className="filters-and-action-buttons-container">
                        {showFilters && (
                            <div className="filter-row">
                                <CommonTableFilterList
                                    tableURL={url}
                                    filters={filterListOptions}
                                    filterValues={filterOptions}
                                    tableRequest={tableRequest}
                                    setTableRequest={setTableRequest}
                                    selectedFilter={selectedFilter}
                                    setSelectedFilter={setSelectedFilter}
                                    clearFilters={clearFilters}
                                    handleClearFilters={handleClearFilters}
                                    setClearFilters={setClearFilters}
                                    filterProps={columnHeaderFilter}
                                />
                            </div>
                        )}
                        <TableActions />
                    </div>
                )
            }

            {rows.length > 0 && <Table />}

            {noData === true && <NoData />}

            {renderBatchUpdate && (
                <BulkModal
                    open={batchUpdateModelOpen}
                    onClose={() => setBatchUpdateModelOpen(false)}
                    onClick={async (excelFile) => {
                        await BatchUpdateImport(batchUpdateType, excelFile);
                        setBatchUpdateModelOpen(false);
                    }}
                    loading={isLoading}
                />
            )}
        </>
    );
};

Table.propTypes = {
    tableId: PropTypes.string,
    url: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    page: PropTypes.number,
    rowCount: PropTypes.number,
    customizeColumnVisible: PropTypes.bool,
    rowActions: PropTypes.array,
    defaultSort: PropTypes.string,
    onRowsUpdated: PropTypes.func,
    refresh: PropTypes.bool,
    onFetchSuccess: PropTypes.func,
    isAllSelected: PropTypes.bool,
    DataCallback: PropTypes.func,
    exportButtonURL: PropTypes.string,
    exportButtonAlt: PropTypes.string,
    fileName: PropTypes.string,
    printButtonURL: PropTypes.string,
    printButtonAlt: PropTypes.string,
    noResultText: PropTypes.string,
    noResultText2: PropTypes.string,
    noResultText3: PropTypes.string,
    showColumnHeaderFilter: PropTypes.bool,
    MUIButtonText: PropTypes.string,
    MUIButtonOnClick: PropTypes.func,
    disableMUIButton: PropTypes.bool,
    tableHeader: PropTypes.string,
    tableHeaderTooltipText: PropTypes.string,
    renderDefaultFilter: PropTypes.bool,
    renderDefaultFilterInTable: PropTypes.bool,
    tableWithoutTitle: PropTypes.bool,
    filterOptions: PropTypes.array,
    subTableColumns: PropTypes.array,
    subtableOpen: PropTypes.bool,
    subtableParentId: PropTypes.string,
    renderBatchUpdate: PropTypes.bool,
    batchUpdateType: PropTypes.string,
    batchUpdateExportText: PropTypes.string,
    batchUpdateImportText: PropTypes.string,
    columnHeaderFilter: PropTypes.object,
    exportFileRef: PropTypes.object,
    hideExportButton: PropTypes.bool,
    showFilters: PropTypes.bool,
    tableContainerSx: PropTypes.object
};

Table.defaultProps = {
    page: 1,
    rowCount: 10,
    customizeColumnVisible: true,
    rowActions: [],
    defaultSort: "",
    onRowsUpdated: () => { },
    refresh: false,
    onFetchSuccess: () => { },
    isAllSelected: false,
    DataCallback: () => { },
    exportButtonURL: "",
    exportButtonAlt: "",
    fileName: "",
    printButtonURL: "",
    printButtonAlt: "",
    noResultText: "",
    noResultText2: "",
    noResultText3: "",
    showColumnHeaderFilter: false,
    MUIButtonText: "",
    MUIButtonOnClick: () => { },
    tableHeader: "",
    tableHeaderTooltipText: "",
    renderDefaultFilter: true,
    renderDefaultFilterInTable: false,
    tableWithoutTitle: false,
    filterOptions: [],
    filterBy: {},
    setFilterBy: () => { },
    subTableColumns: [],
    subtableOpen: false,
    subtableParentId: "",
    renderBatchUpdate: false,
    batchUpdateType: "",
    batchUpdateExportText: "",
    batchUpdateImportText: "",
    columnHeaderFilter: { dateAllowFuture: true },
    hideExportButton: false,
    disableMUIButton: false,
    showFilters: true
};

export default Table;
